import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const WD = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];

import { statusDot } from "../lib/jobStatus";

function parseISO(s) {
    if (!s) return null;
    try { return new Date(s + "T00:00:00"); } catch { return null; }
}

function dateKey(d) {
    return d.toISOString().slice(0, 10);
}

export default function JobsCalendar({ jobs }) {
    const today = new Date();
    const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });

    const days = useMemo(() => {
        const first = new Date(cursor.y, cursor.m, 1);
        const startDow = (first.getDay() + 6) % 7; // 0=Mon
        const last = new Date(cursor.y, cursor.m + 1, 0);
        const totalDays = last.getDate();
        const cells = [];
        for (let i = 0; i < startDow; i++) cells.push(null);
        for (let d = 1; d <= totalDays; d++) cells.push(new Date(cursor.y, cursor.m, d));
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [cursor]);

    const jobsByDay = useMemo(() => {
        const map = {};
        jobs.forEach((j) => {
            const s = parseISO(j.start_date);
            const e = parseISO(j.end_date) || s;
            if (!s) return;
            for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
                const k = dateKey(d);
                map[k] = map[k] || [];
                map[k].push(j);
            }
        });
        return map;
    }, [jobs]);

    const prev = () => setCursor(c => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 });
    const next = () => setCursor(c => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 });
    const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth() });

    const isToday = (d) => d && dateKey(d) === dateKey(today);

    return (
        <div className="bg-white border border-slate-200" data-testid="jobs-calendar">
            <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                <div className="font-display text-xl font-bold text-slate-900">
                    {MONTHS[cursor.m]} {cursor.y}
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={prev} data-testid="cal-prev" className="p-2 border border-slate-300 hover:bg-slate-100 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
                    <button onClick={goToday} data-testid="cal-today" className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Dnes</button>
                    <button onClick={next} data-testid="cal-next" className="p-2 border border-slate-300 hover:bg-slate-100 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
                </div>
            </div>
            <div className="grid grid-cols-7 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                {WD.map((w) => <div key={w} className="px-3 py-2 text-center font-semibold">{w}</div>)}
            </div>
            <div className="grid grid-cols-7">
                {days.map((d, i) => {
                    const k = d ? dateKey(d) : `_${i}`;
                    const items = d ? (jobsByDay[k] || []) : [];
                    return (
                        <div key={k} className={`min-h-[110px] border-r border-b border-slate-100 p-2 ${!d ? "bg-slate-50/50" : ""} ${isToday(d) ? "bg-orange-50/40" : ""}`}>
                            {d && (
                                <>
                                    <div className={`text-xs font-semibold mb-1.5 tabular-data ${isToday(d) ? "text-orange-700" : "text-slate-700"}`}>
                                        {d.getDate()}
                                    </div>
                                    <div className="space-y-1">
                                        {items.slice(0, 3).map((j) => (
                                            <Link key={j.id} to={`/zakazky/${j.id}`}
                                                  data-testid={`cal-event-${j.id}`}
                                                  className={`block ${statusDot(j.status)} text-white text-[10px] px-1.5 py-0.5 rounded-sm truncate hover:opacity-80`}>
                                                {j.title}
                                            </Link>
                                        ))}
                                        {items.length > 3 && <div className="text-[10px] text-slate-500">+ {items.length - 3} dalších</div>}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
